import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { api, type ClientDeposit } from '../api'
import type { FinanceTx, TradingAccount } from '../types'
import {
  financeStatusLabel,
  financeStatusTone,
  financeTypeLabel,
  formatWhen,
  isSelectableAccount,
  maskLogin,
  methodNetworkLabel,
  moneySigned,
  safeError,
} from '../safe-ui'
import { money } from '../prediction-filter'
import { Button } from '../../components/ui/Button'
import { fixtureAccount, fixtureTx, useFixturesFromQuery } from '../fixtures'

type TimelineEvent = {
  event_type?: string
  timestamp?: string
  description?: string
}

function typeLabel(raw?: string | null): string {
  return financeTypeLabel(raw)
}

function statusTone(raw?: string | null): string {
  return financeStatusTone(raw)
}

function statusLabel(raw?: string | null): string {
  return financeStatusLabel(raw)
}

function txDate(tx: FinanceTx): string | undefined {
  return tx.date || tx.created_at
}

export function TransactionsPage() {
  const fixtures = useFixturesFromQuery()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [rows, setRows] = useState<FinanceTx[]>([])
  const [accounts, setAccounts] = useState<TradingAccount[]>([])
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [accountFilter, setAccountFilter] = useState('')
  const [search, setSearch] = useState('')
  const [detail, setDetail] = useState<FinanceTx | null>(null)
  const [timeline, setTimeline] = useState<TimelineEvent[]>([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailWarning, setDetailWarning] = useState('')

  const load = useCallback(async () => {
    if (fixtures) {
      setRows(
        fixtureTx.map((tx) => ({
          ...tx,
          type: 'DEMO_DEPOSIT',
          method: 'USDT_TRC20',
          mt5_login_masked: '***56',
        })),
      )
      setAccounts([fixtureAccount])
      return
    }
    const [txRes, acctRes] = await Promise.all([
      api.transactions({ page: 1, pageSize: 100 }),
      api.listAccounts().catch(() => ({ accounts: [] as TradingAccount[] })),
    ])
    setRows(txRes.transactions || [])
    setAccounts((acctRes.accounts || []).filter((a) => isSelectableAccount(a.status)))
  }, [fixtures])

  useEffect(() => {
    let cancelled = false
    async function run() {
      setLoading(true)
      setError('')
      try {
        await load()
      } catch (err) {
        if (!cancelled) setError(safeError(err, 'Unable to load transactions. Please try again.'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [load])

  const filtered = useMemo(() => {
    return rows.filter((tx) => {
      const created = txDate(tx)
      if (dateFrom && created) {
        const d = new Date(created)
        if (!Number.isNaN(d.getTime()) && d < new Date(`${dateFrom}T00:00:00`)) return false
      }
      if (dateTo && created) {
        const d = new Date(created)
        if (!Number.isNaN(d.getTime()) && d > new Date(`${dateTo}T23:59:59`)) return false
      }
      if (typeFilter) {
        const label = typeLabel(tx.type).toUpperCase()
        if (label !== typeFilter.toUpperCase()) return false
      }
      if (statusFilter) {
        if (String(tx.status || '').toUpperCase() !== statusFilter.toUpperCase()) return false
      }
      if (accountFilter) {
        if (String(tx.trading_account_id || '') !== accountFilter) return false
      }
      if (search.trim()) {
        const q = search.trim().toLowerCase()
        const hay = `${tx.reference || ''} ${tx.type || ''} ${tx.mt5_login_masked || ''}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [rows, dateFrom, dateTo, typeFilter, statusFilter, accountFilter, search])

  const summary = useMemo(() => {
    let deposits = 0
    let withdrawals = 0
    let pending = 0
    let completed = 0
    for (const tx of filtered) {
      const amount = Math.abs(Number(tx.amount) || 0)
      const label = typeLabel(tx.type).toUpperCase()
      const st = String(tx.status || '').toUpperCase()
      if (label === 'DEPOSIT') deposits += amount
      if (label === 'WITHDRAWAL') withdrawals += amount
      if (/(PEND|REVIEW|PROCESS|SUBMIT|CREATED|OPEN)/.test(st)) pending += 1
      if (/(COMPLETE|SETTLED|CREDITED|PAID)/.test(st)) completed += 1
    }
    return { deposits, withdrawals, pending, completed }
  }, [filtered])

  async function openDetail(tx: FinanceTx) {
    setDetail(tx)
    setTimeline([])
    setDetailWarning('')
    setDetailLoading(true)
    try {
      // Reuse deposit detail when reference matches an existing deposit.
      const deposits = await api.listDeposits().catch(() => ({ deposits: [] as ClientDeposit[] }))
      const match = (deposits.deposits || []).find((d) => d.reference && d.reference === tx.reference)
      if (match?.id) {
        const full = await api.getDeposit(match.id)
        setTimeline(full.timeline || [])
        setDetailWarning(full.warning || '')
        setDetail({
          ...tx,
          rejection_reason: full.deposit.rejection_reason || tx.rejection_reason,
          completed_at: full.deposit.completed_at || tx.completed_at,
          status: full.deposit.status || tx.status,
        })
      }
    } catch {
      // Keep list-row detail only — never show technical failure in the drawer.
    } finally {
      setDetailLoading(false)
    }
  }

  function onFilterSubmit(e: FormEvent) {
    e.preventDefault()
  }

  return (
    <section className="cp-page cp-tx">
      {loading ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading…
        </p>
      ) : null}
      {error ? (
        <div className="cp-tx__alert cp-tx__alert--error" role="alert">
          <p>{error}</p>
          <Button type="button" variant="ghost" size="sm" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : null}

      <section
        className={`cp-tx__summary${loading ? ' is-refreshing' : ''}`}
        aria-label="Finance summary"
      >
            <div>
              <span>Total Deposits</span>
              <strong className="cp-tx__num">{money(summary.deposits)} USD</strong>
            </div>
            <div>
              <span>Total Withdrawals</span>
              <strong className="cp-tx__num">{money(summary.withdrawals)} USD</strong>
            </div>
            <div>
              <span>Pending</span>
              <strong className="cp-tx__num">{summary.pending}</strong>
            </div>
            <div>
              <span>Completed</span>
              <strong className="cp-tx__num">{summary.completed}</strong>
            </div>
          </section>

      <form className="cp-tx__filters" onSubmit={onFilterSubmit}>
            <label>
              <span>From</span>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </label>
            <label>
              <span>To</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </label>
            <label>
              <span>Type</span>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All</option>
                <option value="Deposit">Deposit</option>
                <option value="Withdrawal">Withdrawal</option>
                <option value="Refund">Refund</option>
                <option value="Adjustment">Adjustment</option>
              </select>
            </label>
            <label>
              <span>Status</span>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All</option>
                <option value="PENDING">Pending</option>
                <option value="UNDER_REVIEW">Under review</option>
                <option value="COMPLETED">Completed</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </label>
            <label>
              <span>Account</span>
              <select value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)}>
                <option value="">All</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {maskLogin(a.mt5Login)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Search</span>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Reference"
                autoComplete="off"
              />
            </label>
          </form>

      <div className={`cp-tx__table-wrap${loading ? ' is-refreshing' : ''}`}>
            <table className="cp-tx__table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Type</th>
                  <th>Account</th>
                  <th>Amount</th>
                  <th>Currency</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Completed</th>
                  <th>View</th>
                </tr>
              </thead>
              <tbody>
                {!loading && filtered.length === 0 ? (
                  <tr>
                    <td colSpan={10}>No finance transactions yet.</td>
                  </tr>
                ) : (
                  filtered.map((tx, i) => (
                    <tr key={tx.reference || tx.id || i}>
                      <td>{tx.reference || '—'}</td>
                      <td>{typeLabel(tx.type)}</td>
                      <td>{tx.mt5_login_masked || '—'}</td>
                      <td className="cp-tx__num">
                        {moneySigned(tx.amount, tx.currency || 'USD')}
                      </td>
                      <td>{tx.currency || 'USD'}</td>
                      <td>{methodNetworkLabel(tx.method || tx.provider)}</td>
                      <td>
                        <span className={`cp-tx__status cp-tx__status--${statusTone(tx.status)}`}>
                          {statusLabel(tx.status)}
                        </span>
                      </td>
                      <td>{formatWhen(txDate(tx))}</td>
                      <td>{formatWhen(tx.completed_at)}</td>
                      <td>
                        <Button type="button" variant="ghost" size="sm" onClick={() => void openDetail(tx)}>
                          View
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

      <div className={`cp-tx__cards${loading ? ' is-refreshing' : ''}`} aria-label="Transaction cards">
            {!loading && filtered.length === 0 ? (
              <p className="cp-muted">No finance transactions yet.</p>
            ) : (
              filtered.map((tx, i) => (
                <article key={`card-${tx.reference || tx.id || i}`} className="cp-tx__card">
                  <header>
                    <strong>{typeLabel(tx.type)}</strong>
                    <span className={`cp-tx__status cp-tx__status--${statusTone(tx.status)}`}>
                      {statusLabel(tx.status)}
                    </span>
                  </header>
                  <p className={`cp-tx__num ${Number(tx.amount) < 0 ? 'cp-tx__pl--neg' : ''}`}>
                    {moneySigned(tx.amount, tx.currency || 'USD')}
                  </p>
                  <dl>
                    <div>
                      <dt>Reference</dt>
                      <dd>{tx.reference || '—'}</dd>
                    </div>
                    <div>
                      <dt>Date</dt>
                      <dd>{formatWhen(txDate(tx))}</dd>
                    </div>
                  </dl>
                  <Button type="button" variant="secondary" size="sm" onClick={() => void openDetail(tx)}>
                    View
                  </Button>
                </article>
              ))
            )}
          </div>

      {detail ? (
        <div className="cp-tx__drawer-backdrop" role="presentation" onClick={() => setDetail(null)}>
          <aside
            className="cp-tx__drawer"
            role="dialog"
            aria-label="Transaction detail"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="cp-tx__drawer-head">
              <div>
                <p className="cp-eyebrow">Transaction</p>
                <h2>{detail.reference || typeLabel(detail.type)}</h2>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => setDetail(null)}>
                Close
              </Button>
            </header>
            {detailLoading ? <p className="cp-muted">Loading details…</p> : null}
            <dl className="cp-tx__detail-grid">
              <div>
                <dt>Type</dt>
                <dd>{typeLabel(detail.type)}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>
                  <span className={`cp-tx__status cp-tx__status--${statusTone(detail.status)}`}>
                    {statusLabel(detail.status)}
                  </span>
                </dd>
              </div>
              <div>
                <dt>Amount</dt>
                <dd className="cp-tx__num">{moneySigned(detail.amount, detail.currency || 'USD')}</dd>
              </div>
              <div>
                <dt>Account</dt>
                <dd>{detail.mt5_login_masked || '—'}</dd>
              </div>
              <div>
                <dt>Method</dt>
                <dd>{methodNetworkLabel(detail.method || detail.provider)}</dd>
              </div>
              <div>
                <dt>Created</dt>
                <dd>{formatWhen(txDate(detail))}</dd>
              </div>
              <div>
                <dt>Completed</dt>
                <dd>{formatWhen(detail.completed_at)}</dd>
              </div>
              {detail.rejection_reason ? (
                <div>
                  <dt>Rejection reason</dt>
                  <dd>{detail.rejection_reason}</dd>
                </div>
              ) : null}
            </dl>
            {detailWarning ? <p className="cp-note">{detailWarning}</p> : null}
            {timeline.length > 0 ? (
              <ol className="cp-tx__timeline">
                {timeline.map((ev, idx) => (
                  <li key={`${ev.timestamp}-${idx}`}>
                    <strong>{ev.event_type || 'Event'}</strong>
                    <span>{formatWhen(ev.timestamp)}</span>
                    {ev.description ? <p>{ev.description}</p> : null}
                  </li>
                ))}
              </ol>
            ) : null}
          </aside>
        </div>
      ) : null}
    </section>
  )
}
